import React from 'react';
import D3Tooltip from "./D3Tooltip";
import { scaleOrdinal, pie as d3Pie, arc } from 'd3';
import { Box, Grid, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { formatLocaleNumber, truncateText } from 'helpers/emeaHelpers';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
    circle: {
        textTransform: "capitalize",
        cursor: ({ isHyperlink }) => isHyperlink ? "pointer" : "text",
        '&:before': {
            display: "inline-block",
            content: '""',
            marginRight: theme.spacing(.5),
            borderRadius: "50%",
            backgroundColor: ({ color }) => color,
            height: theme.spacing(1.5),
            width: theme.spacing(1.5),
        },
    },
    text: {
        pointerEvents: 'none',
        ...theme.typography.body2
    },
    hyperlink: {
        cursor: 'pointer',
        textDecoration: 'underline',
        color: theme.palette.secondary?.accent,
    },
    link: {
        cursor: 'pointer',
        textDecoration: 'underline',
        letterSpacing: '1px',
        padding: theme.spacing(.4),
        '&:hover': {
            color: theme.palette.link.hover
        }
    },
}));


const Pies = ({ data, radius = 40, setDetails, xKey, tooltipEvent, colorPalette = 'colorPanelEMEA', donutChart = true, showSliceLabel = false, subtype = "", valueType = "" }) => {
    const classes = useStyles();
    const colors = scaleOrdinal()
        .domain(data.map(d => d[xKey]))
        .range(useTheme().palette[colorPalette]);

    const pie = d3Pie()
        .value(d => d.value);

    const pieData = pie(data);

    const arcPath = arc()
        .innerRadius(donutChart ? radius - 25 : 0)
        .outerRadius(radius)


    return pieData.map((item, i) => {
        const tooltipGenerator = typeof tooltipEvent === "function" ? tooltipEvent(item) :
            formatLocaleNumber(item.value);
        const centroid = arcPath.centroid(item);
        return (
            <g key={i}>
                <D3Tooltip
                    placement={"top"}
                    color={colors(item.data[xKey])}
                    title={<div
                        data-testid="pie-tooltip"
                        className={clsx(typeof setDetails === "function" ? classes.link : {})}
                        onClick={() => {
                            if (typeof setDetails === "function") {
                                setDetails({ ...item.data, valueType })
                            }
                        }}>
                        {item.data[xKey]} : {tooltipGenerator}
                    </div>}>
                    <path
                        d={arcPath(item)}
                        fill={colors(item.data[xKey])}
                        data-testid={`id-${item.data[xKey]}`}
                        value={item.value}
                    />
                </D3Tooltip>
                {showSliceLabel && <text
                    transform={`translate(${centroid[0]},${centroid[1]})`}
                    textAnchor="middle"
                    className={classes.text}
                    fill="black"
                    dy={5}
                    fontSize={8.5}
                    data-testid={`arc-label-${i}`}
                >{item.value}</text>}
            </g>
        )
    })
}

export const LegendText = ({
    label,
    color,
    style = "circle",
    setDetails,
    data,
    isHyperlink,
    tooltipLabel,
    tooltipExclusion
}) => {
    const classes = useStyles({ color, isHyperlink });
    return (
        <Grid item data-testid="legend-text">
            <D3Tooltip title={<Typography
                component='p'
                color={color}
                className={classes[style]}
                data-color={color}>
                {label}
            </Typography>}
                placement="top" themeType={"light"}>
                <Typography
                    component='p'
                    variant='body2'
                    color={color}
                    data-testid={`linkid-${label}`}
                    onClick={() => {
                        if (isHyperlink && !tooltipExclusion.includes(data?.state)) {
                            setDetails(data)
                        }
                    }}
                    className={{
                        [classes[style]]: true,
                        [classes.hyperlink]: !tooltipExclusion.includes(data?.state) && isHyperlink,
                    }}
                    data-color={color}>
                    {truncateText(tooltipLabel)}
                </Typography>
            </D3Tooltip>
        </Grid>
    )
}

export const Legends = ({
    data,
    xKey,
    colorScale,
    setDetails,
    tooltipKey = null,
    displayKey = null,
    tooltipExclusion = [],
    viewDetails,
    colorPalette = 'colorPanelEMEA',
    isLegendClickable = true,
}) => {
    const colorRange = useTheme().palette[colorPalette];

    let colorPanel = scaleOrdinal().domain(data.map(d => d[xKey])).range(colorRange);
    const isHyperlink = typeof setDetails === "function" && isLegendClickable;
    
    if (typeof colorScale === "function") {
        colorPanel = colorScale
    }

    return (<Box p={2} m={viewDetails ? 0 : 1}>
        <Grid
            container
            alignItems="center"
            justify="center"
            spacing={2}
            data-testid="legend-wrapper"
        >
            {
                data?.map(d => {
                    return <LegendText
                        key={d[xKey]}
                        label={d[displayKey || xKey]}
                        tooltipLabel={d[tooltipKey || xKey]}
                        color={colorPanel(d[xKey])}
                        xs={4}
                        isHyperlink={isHyperlink}
                        setDetails={setDetails}
                        data={d}
                        tooltipExclusion={tooltipExclusion}
                    />
                })
            }
        </Grid>

    </Box>)
}

export const PieLabel = ({ title, subtitle, labelWidth = 50, donutTitle = true }) => {
    const labelHeight = 40;
    return (
        <g data-testid={`pielabel-${title}`}>
              { donutTitle  ? 
                <foreignObject height={labelHeight} width={labelWidth} transform={`translate(${-labelWidth / 2}, ${-labelHeight / 2})`} >
                    <Typography style={{ textAlign: "center" }} variant="body2" color="textSecondary">{title}</Typography>
                    <Typography style={{ textAlign: "center" }} variant="body2" color="textSecondary">{subtitle}</Typography>
                </foreignObject>
            :  <foreignObject height={labelHeight} width={labelWidth} transform={`translate(${-labelWidth / 2}, ${-labelHeight / 2})`} >
              <Typography style={{ textAlign: "center", fontSize: "20px", color:"white" }} variant="body2" color="textSecondary">{subtitle}%</Typography>  
          </foreignObject> }
        </g>
    )
}

export default Pies;